/**
 * CW-SF-D1 TKT-5 — Recent Team Activity widget.
 *
 * Last 10 SF activity-feed entries visible to the viewer with no
 * OwnerId filter. Reuses M4's activity_feed table + visibility
 * filter unchanged; presents at tile-size with the actor name +
 * action verb + resource link.
 */
import { useCallback } from 'react';
import { ExternalLink } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { useAutoRefresh } from '@/services/widgetRegistry';
import type { WidgetProps } from '@/types';
import { SfWidgetShell } from './SfWidgetShell';
import { buildSfWidgetUrl, formatRelativeMinutes, type SfWidgetEnvelope } from './sfWidgetUtils';

interface ActivityItem {
  id: string;
  action_type: string;
  actor_name: string | null;
  resource_name: string;
  resource_url: string | null;
  activity_timestamp: string | null;
  provider_type: string;
}
interface ApiResponse extends SfWidgetEnvelope {
  items: ActivityItem[];
}

const REFRESH_S = 5 * 60;

const ACTION_VERB: Record<string, string> = {
  created: 'created',
  modified: 'modified',
  deleted: 'deleted',
  shared: 'shared',
  approved: 'approved',
  rejected: 'rejected',
  acknowledged: 'acknowledged',
  tagged: 'tagged',
  classified: 'classified',
};

export function SfTeamRecentActivityWidget({ config }: WidgetProps) {
  const fetcher = useCallback(
    () => apiFetch<ApiResponse>(buildSfWidgetUrl('/mod/cw/widgets/sf/team-recent-activity', config)),
    [config],
  );
  const { data, loading, error } = useAutoRefresh<ApiResponse>(fetcher, REFRESH_S);

  return (
    <div className="sf-widget sf-widget--activity">
      <SfWidgetShell
        envelope={data}
        loading={loading}
        error={error}
        hasItems={(data?.items?.length ?? 0) > 0}
        emptyMessage="No recent Salesforce activity visible to you."
      >
        <ul className="sf-widget__list">
          {data?.items.map((a) => {
            const verb = ACTION_VERB[a.action_type] ?? a.action_type;
            return (
              <li key={a.id} className="sf-widget__row sf-widget__row--activity">
                <div className="sf-widget__activity-line">
                  <span className="sf-widget__chip sf-widget__chip--owner">
                    {a.actor_name ?? 'Someone'}
                  </span>
                  <span className="sf-widget__activity-verb">{verb}</span>
                  {a.resource_url ? (
                    <a
                      href={a.resource_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="sf-widget__row-name"
                    >
                      {a.resource_name}
                      <ExternalLink size={10} />
                    </a>
                  ) : (
                    <span className="sf-widget__row-name">{a.resource_name}</span>
                  )}
                </div>
                <span className="sf-widget__activity-when">
                  {formatRelativeMinutes(a.activity_timestamp)}
                </span>
              </li>
            );
          })}
        </ul>
      </SfWidgetShell>
    </div>
  );
}
